/**
 * 
 * @prettier
 */

import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
const BaseTransitionContext = /*#__PURE__*/React.createContext('in');
export function useTransitionContext() {
  return useContext(BaseTransitionContext);
}
const priority = ['out', 'leaving', 'appearing', 'entering'];
export function overallTransitionState(parentState, childState) {
  for (let state of priority) {
    if (parentState === state || childState === state) return state;
  }
  return childState;
}
export function TransitionContext({
  state,
  children
}) {
  const parentState = useTransitionContext();
  const overallState = overallTransitionState(parentState, state);
  return /*#__PURE__*/React.createElement(BaseTransitionContext.Provider, {
    value: overallState
  }, children);
}
function outish(state) {
  return state === 'out' || state === 'leaving';
}
export function useTransitionStateEffect(effect) {
  const nextState = useTransitionContext();
  const prevStateRef = useRef(null);
  const effectRef = useRef(effect);
  effectRef.current = effect;
  useEffect(() => {
    const prevState = prevStateRef.current;
    const effect = effectRef.current;
    prevStateRef.current = nextState;
    effect(prevState, nextState);
  }, [nextState]);
  useEffect(() => {
    return () => {
      const effect = effectRef.current;
      if (!outish(nextState)) effect(nextState, 'leaving');
    };
  }, []);
}
export function useTransitionStateEffectFilter(filter) {
  return effect => useTransitionStateEffect((prevState, nextState) => {
    if (filter(prevState, nextState)) effect(prevState, nextState);
  });
}
export const useAppearingEffect = useTransitionStateEffectFilter((prevState, nextState) => outish(prevState || 'out') && nextState === 'appearing');
export const useEnteringEffect = useTransitionStateEffectFilter((prevState, nextState) => outish(prevState || 'out') && nextState === 'entering');
export const useAppearedEffect = useTransitionStateEffectFilter((prevState, nextState) => prevState === 'appearing' && nextState === 'in');
export const useEnteredEffect = useTransitionStateEffectFilter((prevState, nextState) => prevState === 'entering' && nextState === 'in');
export const useCameInEffect = useTransitionStateEffectFilter((prevState, nextState) => nextState === 'in');
export const useLeavingEffect = useTransitionStateEffectFilter((prevState, nextState) => !outish(prevState || 'out') && nextState === 'leaving');
export const useLeftEffect = useTransitionStateEffectFilter((prevState, nextState) => prevState === 'leaving' && nextState === 'out');
export function useAutofocusRef() {
  const ref = useRef();
  useCameInEffect(() => {
    const el = ref.current;
    if (el) {
      el.focus();
      if (typeof el.select === 'function') el.select();
    }
  });
  return ref;
}